/**
 * Builds an expo-sqlite-shaped connection in EVERY tab that routes each call to
 * the leader worker over the leader-resource client.
 *
 * The tab holds a PROXY object with the same method names; the real
 * `TursoExpoSqliteAdapter` never crosses the boundary — it stays with the engine
 * in the leader worker, along with the connection lock and the busy-retry
 * wrapper (see `open-leader-turso-resource.web.ts` for why those must be exactly
 * one instance process-wide).
 *
 * `attachSqliteChangeFeed` and `createPerformanceLogger` stay per-tab, wrapped
 * around this proxy by the caller — the change feed emits into the tab's own
 * invalidation bus, and the logger is keyed to the calling tab's console.
 *
 * CROSS-TAB CHANGE FAN-OUT (§4.6) closes the gap that leaves: the leader emits a
 * `table-changed` event per mutating statement it executes and per `pull()`, and
 * the `onEvent` handler below feeds each one into `emitManualDatabaseChange` —
 * the SAME in-tab bus `attachSqliteChangeFeed` writes to. So a write in Tab A
 * invalidates Tab B's queries through the existing machinery, unchanged. Events
 * stamped with THIS tab's id are dropped, because this tab's own change feed
 * already fired for them locally.
 */
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { emitManualDatabaseChange, extractMutatedTable } from "@-/com.npmjs.expo-sqlite";
import {
  createLeaderResourceClient,
  isLeaderChangedError,
  LeaderChangedError,
  type LeaderResourceClient,
  type LeaderResourceEvent,
  type LeaderResourceStatus,
} from "@-/dev.improve.haniverse.lib.leader-resource";
import {
  parseTableChangedPayload,
  shouldApplyTableChangedPayload,
  TABLE_CHANGED_EVENT_KIND,
} from "#turso-change-event";
import type { SQLiteDatabase } from "#open-sqlite-database";
import type {
  ActorTursoSyncConnection,
  ActorTursoSyncStats,
} from "#open-actor-turso-sync-connection-types";
import { recordLeaderOpenTrace } from "#leader-open-trace";
import type { ActorSchemaUpdate } from "#update-actor-schema";
import type { TursoLeaderApi } from "#open-leader-turso-resource";
import { TURSO_LEADER_METHODS, TURSO_LEADER_PAUSE_CONTROL } from "#open-leader-turso-resource";
import { classifyTransactionControl } from "#actor-sync-connection-lock";
import {
  classifyLeaderRequestResendability,
  createLeaderTransactionState,
} from "#classify-leader-request-resendability";
import { createOrphanedTransactionTracker } from "#track-orphaned-transaction";
import { getLeaderCallControl, parseLeaderTrackedCall } from "#leader-tracked-call";
import { createTursoLeaderWorker } from "#create-turso-leader-worker";
import { createLeaderCallQueue } from "#create-leader-call-queue";

/**
 * Guest handover budget. The new leader's `open()` ladder is ~1.5s
 * (`OPEN_MAX_ATTEMPTS = 6` x `OPEN_RETRY_DELAY_MS = 250`), so 20s covers it
 * comfortably including a couple of failed-leadership handoffs.
 */
const GUEST_HANDOVER_BUDGET_MS = 20_000;

/**
 * Actor handover budget — THREE TIMES the guest one, and deliberately not shared
 * with it.
 *
 * The actor `open()` is far slower than the guest one: it is a network bootstrap,
 * not a local file open. Its ladder is up to ~36s (`CONNECT_TIMEOUT_MS = 12_000`
 * x `CONNECT_MAX_ATTEMPTS = 3`) plus a `wipeTursoDatabaseFiles` between attempts.
 *
 * 60s also sits ABOVE the leader's own connect ladder, which is the point: a
 * genuinely unreachable sync server surfaces from the leader's `open()` as
 * "Turso sync connect timed out" (naming the real problem) before this budget
 * can turn it into "no database leader available" (naming only the symptom).
 */
const ACTOR_HANDOVER_BUDGET_MS = 60_000;

/** Leading whitespace run, used to pull the first SQL keyword for the marker. */
const SQL_WHITESPACE_RE = /\s+/;

/** Unique across tabs, workers, and reloads; persisted with the write effect. */
function mintWriteId(): string {
  return crypto.randomUUID();
}

/** Report handovers that could not be resolved by the durable receipt protocol. */
function reportLostWrite(
  method: string,
  args: readonly unknown[],
  error: unknown,
  writeId: string,
): void {
  if (!isLeaderChangedError(error)) {
    return;
  }
  const source = typeof args[0] === "string" ? args[0] : "";
  const mutatedTable = extractMutatedTable(source);
  logger(import.meta.location).warn("leader-lost-write", {
    method,
    mutatedTable,
    sqlKind: source.trim().split(SQL_WHITESPACE_RE)[0]?.toLowerCase() ?? "",
    // A lost `commit` names no table but is the most consequential lost write
    // of all — the whole transaction may or may not have landed. Counting it as
    // non-mutating would under-report exactly the rate this marker exists to
    // measure.
    wasMutating: mutatedTable !== null || classifyTransactionControl(source) === "commit",
    writeId,
  });
}

/**
 * Feed one leader-broadcast event into THIS tab's local change bus.
 *
 * Deliberately re-uses `emitManualDatabaseChange` rather than introducing a
 * parallel notification path: every existing invalidation consumer already
 * listens to that bus (React Query invalidation, the actor auto-push debounce,
 * the write clocks), so a cross-tab change arrives on exactly the same wire as a
 * same-tab one and needs no consumer to know the difference.
 *
 * Two filters, both load-bearing:
 *   - Unknown `kind`s are ignored. The event channel is shared per resource, so a
 *     future event type must not be mistaken for a change.
 *   - ORIGIN-TAB DEDUPE: an event whose `originTabId` is ours is our own echo.
 *     `attachSqliteChangeFeed` wraps THIS proxy, so it already emitted this exact
 *     change synchronously at statement completion, before the RPC returned.
 *
 * `rowId` is re-materialized as `0` rather than shipped: the local feed only ever
 * carries a meaningful `lastInsertRowId` for the inserting connection, and every
 * consumer of this bus invalidates by TABLE. See `turso-change-event.ts`.
 */
function applyLeaderChangeEvent(event: LeaderResourceEvent, receivingTabId: string): void {
  if (event.kind !== TABLE_CHANGED_EVENT_KIND) {
    return;
  }
  const payload = parseTableChangedPayload(event.payload);
  if (payload === null || !shouldApplyTableChangedPayload(payload, receivingTabId)) {
    return;
  }
  emitManualDatabaseChange({
    databaseFilePath: payload.databaseFilePath,
    databaseName: payload.databaseName,
    rowId: 0,
    tableName: payload.tableName,
  });
}

interface ProxiedStatement {
  executeAsync(params?: unknown): Promise<{
    changes: number;
    getAllAsync(): Promise<unknown[]>;
    getFirstAsync(): Promise<unknown>;
    lastInsertRowId: number;
  }>;
  executeForRawResultAsync(params?: unknown): Promise<{ getAllAsync(): Promise<unknown[]> }>;
  finalizeAsync(): Promise<void>;
}

export interface ProxiedTursoConnection {
  /** Detach this tab (relinquishing leadership if this tab holds it). */
  close(): Promise<void>;
  connection: SQLiteDatabase;
  /** True when THIS tab's worker won the election. */
  readonly isLeader: boolean;
  /**
   * SQL statements this tab has issued through the proxy since it opened
   * (`pull`/`push` and the wipe controls excluded). Observability for the
   * per-query round-trip model (plan §7.1) — a live counter, not a metric that
   * notifies anyone; read it when you want it.
   */
  readonly queryCount: number;
  /**
   * The sign-in wipe primitive (§5.3): close the leader's engine while it KEEPS
   * the election lock, run `wipe`, then reopen. Holding the lock across the
   * window is what stops another tab from electing itself and re-opening
   * `actor.db` while it is being deleted.
   *
   * Takes the wipe as a callback rather than exposing close/reopen separately so
   * the reopen cannot be skipped: a caller that closed and then threw would
   * otherwise leave every tab queued against a permanently closed engine until
   * the handover budget expired.
   */
  runWithEngineClosed(wipe: () => Promise<void>): Promise<void>;
  /** This tab's current leadership/connection state, for a debug indicator. */
  readonly status: LeaderResourceStatus;
  /** Notified on every `status` transition. Returns an unsubscribe. */
  subscribeStatus(listener: (status: LeaderResourceStatus) => void): () => void;
  /**
   * Proxied `{ pull, push }`, or `null` for a local-only (guest) database.
   *
   * These are the reason `pull`/`push` are on the leader allowlist at all: CDC is
   * PER-CONNECTION, so only the leader's connection holds the changes every tab
   * wrote. A tab pushing through its own engine would ship an empty log.
   */
  sync: Pick<ActorTursoSyncConnection["tursoDb"], "pull" | "push" | "stats"> | null;
  /** This tab's identity in the election protocol; stable across handovers. */
  readonly tabId: string;
  /**
   * Bring the database up to THIS tab's migrations. The leader migrated it to its own at open; a
   * tab that carries a newer build asks for the rest, so tabs on different builds converge on the
   * newest schema (migrations are additive, so the older tab keeps working).
   */
  updateSchema(migrations: Readonly<Record<string, string>>): Promise<ActorSchemaUpdate>;
}

/** Normalize expo-sqlite's `(sql, ...params)` / `(sql, paramsArray)` calling styles. */
function normalizeParams(rest: readonly unknown[]): unknown[] {
  if (rest.length === 1 && Array.isArray(rest[0])) {
    return [...rest[0]];
  }
  return [...rest];
}

/** Credentials `actor.db`'s leader `open()` needs; absent for the guest database. */
export interface ProxiedTursoSyncOptions {
  authToken: string;
  gatewayUrl: string;
  url: string;
}

interface OpenProxiedTursoConnectionOptions {
  /**
   * Whether `connection.closeAsync()` detaches this tab from the leader.
   *
   * `true` (guest) keeps the drop-in expo-sqlite semantics: whoever closes the
   * connection is done with it. `false` (actor) hands lifetime control to the
   * caller, because the sign-in wipe closes the connection and then still needs a
   * live client to drive `runWithEngineClosed` — see the note on `closeAsync`.
   */
  detachOnCloseAsync: boolean;
  handoverBudgetMs: number;
  resourceId: string;
  /**
   * Present only for a SYNC database. Forwarded to this tab's own worker as the
   * `openContext`, so it is used only if THIS tab wins the election. Sent by
   * `postMessage`, never as a worker-URL parameter: it carries an auth token.
   */
  sync: ProxiedTursoSyncOptions | null;
}

/** One breadcrumb per election status, keeping a failed leader's reason. */
function describeLeaderStatusForTrace(status: LeaderResourceStatus): string {
  switch (status.state) {
    case "connected": {
      return status.isLeader ? "connected(leader)" : "connected(follower)";
    }
    case "failed": {
      return `failed(${status.reason})`;
    }
    case "handover": {
      return `handover(queued=${status.queuedCount})`;
    }
    case "electing": {
      return "electing";
    }
  }
}

/**
 * Open a connection that proxies every call to whichever tab currently leads.
 *
 * Resolves once this tab is attached to a leader that has SUCCESSFULLY OPENED the
 * database — `leader-here` is emitted only after `open()` resolves, so a resolved
 * `ready()` means the file is genuinely usable, not merely claimed.
 */
async function openProxiedTursoConnection(
  options: OpenProxiedTursoConnectionOptions,
): Promise<ProxiedTursoConnection> {
  const { detachOnCloseAsync } = options;
  recordLeaderOpenTrace(options.resourceId, "open");
  const workerHandle = await createTursoLeaderWorker(options.resourceId);
  recordLeaderOpenTrace(options.resourceId, "worker");
  const { worker } = workerHandle;
  // Transaction depth is derived from the dispatch stream, so ONE state per
  // connection (the queue calls the predicate exactly once per request, in
  // dispatch order — see `isResendable`'s contract).
  const transactionState = createLeaderTransactionState();
  // Call-time counterpart of `transactionState`: decides, BEFORE a statement is
  // enqueued, whether it belongs to a transaction that died with its leader.
  // See `track-orphaned-transaction.ts` for why dispatch-time is too late.
  const orphanedTransactions = createOrphanedTransactionTracker();
  const statusListeners = new Set<(status: LeaderResourceStatus) => void>();
  let queryCount = 0;
  let transactionId: string | null = null;
  let transactionCreatedAt: number | null = null;
  let client: LeaderResourceClient<TursoLeaderApi>;
  try {
    client = createLeaderResourceClient<TursoLeaderApi>({
      handoverBudgetMs: options.handoverBudgetMs,
      isResendable: (entry) => classifyLeaderRequestResendability(transactionState, entry),
      methods: TURSO_LEADER_METHODS,
      pauseControl: TURSO_LEADER_PAUSE_CONTROL,
      // CROSS-TAB CHANGE FAN-OUT (§4.6). `client.tabId` is read LAZILY inside the
      // handler rather than captured above it, because `client` is still being
      // constructed here — and it is safe: no event can arrive before the client
      // exists, since the channels it listens on are opened by that constructor.
      onEvent: (event) => {
        applyLeaderChangeEvent(event, client.tabId);
      },
      onStatusChange: (status) => {
        recordLeaderOpenTrace(options.resourceId, describeLeaderStatusForTrace(status));
        if (status.state === "handover") {
          // Whether a NEW leader was elected or the SAME leader paused its
          // engine (the sign-in wipe), the engine that held any open
          // transaction is gone. Two consequences, both applied here because
          // this is the one place that learns of it synchronously:
          //   - this tab's open transaction (if any) is now orphaned, so its
          //     remaining statements must not run autocommit on the new engine;
          //   - the dispatch-time depth tracker restarts from zero, since the
          //     new engine has no transaction open. Without this reset a lost
          //     transaction whose `rollback` was answered locally (never
          //     dispatched) would leave the depth stuck above zero, and every
          //     later read on this connection would be classified as
          //     inside-a-transaction — never re-sendable — for the tab's life.
          orphanedTransactions.markHandover();
          transactionState.depth = 0;
        }
        for (const listener of statusListeners) {
          listener(status);
        }
      },
      openContext: options.sync ?? undefined,
      resourceId: options.resourceId,
      worker,
    });
    await client.ready();
    recordLeaderOpenTrace(options.resourceId, "ready");
  } catch (error) {
    recordLeaderOpenTrace(
      options.resourceId,
      `open-failed: ${error instanceof Error ? error.message : String(error)}`,
    );
    workerHandle.dispose();
    throw error;
  }

  async function closeClientAndWorker(): Promise<void> {
    try {
      await client.close();
    } finally {
      workerHandle.dispose();
    }
  }

  // Serialize through acknowledgement, not just execution in the worker. Otherwise a
  // concurrent read can capture COMMIT's old transaction ID after the worker closes
  // that transaction but before this proxy processes its acknowledgement.
  const calls = createLeaderCallQueue(async () => {});
  async function call<T>(method: string, args: readonly unknown[]): Promise<T> {
    const control = getLeaderCallControl(args);
    // Sync takes the engine lock held by BEGIN. Let COMMIT/ROLLBACK pass a
    // waiting sync call, or neither RPC can acknowledge and this tab deadlocks.
    const result = await calls.run(
      "connection",
      control === "begin" || method === "closeEngineHoldingLeadership",
      control === "commit" || control === "rollback" || method === "reopenEngine",
      () => executeCall<T>(method, args),
      method === "push" || method === "pull" || method === "stats" || method === "updateSchema",
    );
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- The queue preserves the callback result; its shared interface erases the generic.
    return result as T;
  }

  /** One round trip, with the lost-write marker on a handover rejection. */
  async function executeCall<T>(method: string, args: readonly unknown[]): Promise<T> {
    const sql = typeof args[0] === "string" ? args[0] : null;
    if (
      transactionId !== null &&
      !orphanedTransactions.orphaned &&
      getLeaderCallControl(args) === "begin"
    ) {
      // Concurrent top-level callbacks share this proxy. Refuse the second BEGIN
      // before replacing the first callback's identity; its existing busy retry
      // can start it after the first transaction settles. Savepoints do not BEGIN.
      throw new Error("database is locked: another transaction is active on this tab");
    }
    if (sql !== null) {
      queryCount += 1;
    }
    switch (orphanedTransactions.classify(sql)) {
      case "skip": {
        if (getLeaderCallControl(args) === "rollback") {
          transactionId = null;
          transactionCreatedAt = null;
        }
        // drizzle's unwind of a transaction that died with its leader. There is
        // nothing on the new leader to roll back; answering locally is what
        // lets the ORIGINAL `LeaderChangedError` escape `transaction()` instead
        // of a "no transaction is active" from the new leader replacing it.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- `rollback` returns no rows; a void result is what the caller expects
        return undefined as T;
      }
      case "orphaned": {
        // Refused without dispatch: it would run in autocommit on the new
        // leader, outside the transaction the caller believes it is in.
        throw new LeaderChangedError(
          "Leader changed while a transaction was open; the transaction was rolled back with its leader",
        );
      }
      case "dispatch": {
        break;
      }
    }
    const control = sql === null ? null : getLeaderCallControl(args);
    const writeId = mintWriteId();
    if (control === "begin") {
      transactionId = writeId;
      transactionCreatedAt = Date.now();
    }
    let confirmedRollback = false;
    try {
      /* eslint-disable typescript/no-unsafe-type-assertion -- the allowlisted method's return type is the caller's contract */
      const response =
        sql === null
          ? { outcome: "applied" as const, result: await Reflect.get(client.api, method)(...args) }
          : await client.api.executeTracked(
              parseLeaderTrackedCall({
                args,
                createdAt: transactionCreatedAt ?? Date.now(),
                method,
                transactionId,
                writeId,
              }),
            );
      /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */
      if (response.outcome === "rolled-back") {
        confirmedRollback = true;
        orphanedTransactions.markHandover();
        throw new LeaderChangedError(
          "The previous leader rolled back this transaction; retry the whole transaction",
        );
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- result has the SQL method's original return shape
      const result = response.result as T;
      orphanedTransactions.settle(sql, null);
      if (control === "commit" || control === "rollback") {
        transactionId = null;
        transactionCreatedAt = null;
      }
      return result;
    } catch (error) {
      // Order matters: `settle` stamps a lost `commit` not-replayable, and the
      // marker below should describe the error as the caller will receive it.
      // A durable absence on the new leader proves this COMMIT did not apply.
      // Keep it replayable; only an indeterminate commit gets the no-replay stamp.
      orphanedTransactions.settle(confirmedRollback ? "SELECT 1" : sql, error);
      if (control === "begin") {
        transactionId = null;
        transactionCreatedAt = null;
      }
      if (!confirmedRollback) {
        reportLostWrite(method, args, error, writeId);
      }
      throw error;
    }
  }

  const connection = {
    closeAsync: async (): Promise<void> => {
      // Semantics CHANGE here versus the direct adapter, TWICE over.
      //
      // 1. A proxy tab must NOT close the shared engine — that would rip the
      //    database out from under every other tab.
      // 2. It must not detach this tab EITHER, when a `detachOnCloseAsync: false`
      //    consumer owns the lifetime explicitly. The actor path does: its
      //    sign-in sequence calls `closeCachedActorDatabaseConnection()` (which
      //    lands here) and THEN needs a live client to drive the wipe's
      //    close/reopen through `runWithEngineClosed`. Detaching here would
      //    terminate the worker before that, and — because detaching also
      //    relinquishes leadership — would let another tab re-open `actor.db`
      //    on top of the file being deleted, silently defeating the wipe.
      //
      // So this is a deliberate no-op for such consumers; they call `close()`
      // themselves once the window is over.
      if (!detachOnCloseAsync) {
        return;
      }
      await closeClientAndWorker();
    },
    execAsync: async (source: string): Promise<void> => call("execAsync", [source]),
    executeBackupRestore: (
      statements: import("#actor-backup-reader").ActorBackupRestoreStatement[],
    ) => call("executeBackupRestore", [statements]),
    getAllAsync: async (source: string, ...rest: unknown[]): Promise<unknown[]> =>
      call("getAllAsync", [source, normalizeParams(rest)]),
    getEachAsync: (): never => {
      // Deliberate throw, NOT an omission. `getEachAsync` holds a LIVE statement
      // across yields, which cannot survive a `postMessage` boundary — the
      // flattening that works for every other method (re-issuing the SQL per
      // call) would silently change iteration semantics. No app code calls it
      // (only wrappers and codemod fixtures reference it), so failing loudly is
      // strictly better than a plausible-looking wrong answer.
      throw new TypeError(
        "getEachAsync is not supported across the leader boundary (it holds a live statement)",
      );
    },
    getFirstAsync: async (source: string, ...rest: unknown[]): Promise<unknown> =>
      call("getFirstAsync", [source, normalizeParams(rest)]),
    prepareAsync: async (source: string): Promise<ProxiedStatement> => {
      // FLATTENED. The adapter's statement object cannot cross `postMessage`, and
      // it never holds engine-side state between calls (verified against
      // sync-wasm 0.7.2 — see `open-leader-turso-resource.web.ts`), so each
      // method below is one independent round trip carrying `(sql, params)`.
      // This mirrors what the adapter already does: its sub-readers re-issue
      // `tursoDb.all/get` with the same source and bind, and document themselves
      // as "standalone reads".
      const statement: ProxiedStatement = {
        executeAsync: async (params?: unknown) => {
          const bind = Array.isArray(params) ? [...params] : [];
          const result = await call<{ changes: number; lastInsertRowId: number }>("runAsync", [
            source,
            bind,
          ]);
          return {
            changes: result.changes,
            getAllAsync: async () => call<unknown[]>("getAllAsync", [source, bind]),
            getFirstAsync: async () => call<unknown>("getFirstAsync", [source, bind]),
            lastInsertRowId: result.lastInsertRowId,
          };
        },
        executeForRawResultAsync: async (params?: unknown) => {
          const bind = Array.isArray(params) ? [...params] : [];
          const tuples = await call<unknown[][]>("prepareValuesAsync", [source, bind]);
          return { getAllAsync: async () => tuples };
        },
        finalizeAsync: async () => {
          // No-op, exactly as today: Turso's `Database.run/all/get` finalize
          // their own prepared statements, so there is nothing to release —
          // and with the flattening there is no remote handle either.
        },
      };
      return statement;
    },
    readBackupSnapshot: (tables: readonly string[]) => call("readBackupSnapshot", [tables]),
    runAsync: async (
      source: string,
      ...rest: unknown[]
    ): Promise<{ changes: number; lastInsertRowId: number }> =>
      call("runAsync", [source, normalizeParams(rest)]),
    setBackupRecoveryHold: (enabled: boolean) => call("setBackupRecoveryHold", [enabled]),
  };

  return {
    close: async () => {
      await closeClientAndWorker();
    },
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the proxy implements the expo-sqlite surface the codebase calls
    connection: connection as unknown as SQLiteDatabase,
    get isLeader() {
      return client.isLeader;
    },
    get queryCount() {
      return queryCount;
    },
    runWithEngineClosed: async (wipe: () => Promise<void>) => {
      // The leader broadcasts `leader-paused` before running this and
      // `leader-resumed` after the reopen succeeds, so every OTHER tab queues
      // instead of dispatching into a closed engine — WITHOUT re-electing, since
      // leadership never changes. That is what makes the window exclusive.
      await call<void>("closeEngineHoldingLeadership", []);
      try {
        // Runs in the PAGE, not the leader: OPFS's root is shared across the
        // whole origin, and the handle that blocked `removeEntry` was just
        // released by the close above.
        await wipe();
      } finally {
        // `finally`, so a failed wipe still reopens. Leaving the engine closed
        // would strand every tab queued until its handover budget expired, which
        // converts a recoverable wipe failure into a dead app.
        await call<void>("reopenEngine", []);
      }
    },
    get status() {
      return client.status;
    },
    subscribeStatus: (listener) => {
      statusListeners.add(listener);
      return () => {
        statusListeners.delete(listener);
      };
    },
    sync:
      options.sync === null
        ? null
        : {
            pull: async () => call<boolean>("pull", []),
            push: async () => call<void>("push", []),
            stats: async () => call<ActorTursoSyncStats>("stats", []),
          },
    get tabId() {
      return client.tabId;
    },
    updateSchema: async (migrations) => call<ActorSchemaUpdate>("updateSchema", [migrations]),
  };
}

/**
 * Signed-out (guest) database. Local-only by construction — no url is ever
 * passed, so `sync` comes back `null`.
 */
export async function openProxiedGuestTursoConnection(
  resourceId: string,
): Promise<ProxiedTursoConnection> {
  return openProxiedTursoConnection({
    // Drop-in expo-sqlite semantics: the guest path has no wipe window, so
    // closing the connection legitimately means "this tab is done".
    detachOnCloseAsync: true,
    handoverBudgetMs: GUEST_HANDOVER_BUDGET_MS,
    resourceId,
    sync: null,
  });
}

/**
 * Signed-in (sync) database. Gets a THREE TIMES larger handover budget than the
 * guest path because its open is a network bootstrap rather than a local file
 * open; see `ACTOR_HANDOVER_BUDGET_MS`.
 */
export async function openProxiedActorTursoConnection(
  resourceId: string,
  sync: ProxiedTursoSyncOptions,
): Promise<ProxiedTursoConnection> {
  return openProxiedTursoConnection({
    // The caller owns the lifetime: `closeCachedActorDatabaseConnection()` runs
    // BEFORE the sign-in wipe, which still needs this client alive to drive
    // `runWithEngineClosed`. See `closeAsync`'s note and
    // `detachProxiedActorConnection` in `actor-database.web.ts`.
    detachOnCloseAsync: false,
    handoverBudgetMs: ACTOR_HANDOVER_BUDGET_MS,
    resourceId,
    sync,
  });
}
